import React from 'react';

/**
 * Shared look for the Brand Kit strategy, plan and media panels: a bold display heading with
 * an eyebrow and one line of explanation, and small chips. Kept here so every panel reads the
 * same way (Plus Jakarta Sans display headings, Inter body, Admeru orange/navy accents).
 */

export const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <section className={`bg-card rounded-2xl border border-border p-5 sm:p-6 space-y-5 ${className}`}>{children}</section>
);

export const SectionHeader: React.FC<{
  eyebrow?: string; title: string; description?: React.ReactNode; actions?: React.ReactNode; icon?: React.ReactNode;
}> = ({ eyebrow, title, description, actions, icon }) => (
  <header className="flex flex-wrap items-start justify-between gap-3">
    <div className="min-w-0 space-y-1.5">
      {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-primary">{eyebrow}</p>}
      <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight text-ink flex items-center gap-2 [text-wrap:balance]">
        {icon}{title}
      </h2>
      {description && <p className="text-sm text-ink-2 max-w-2xl">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </header>
);

type Tone = 'neutral' | 'accent' | 'navy' | 'success' | 'warning' | 'info';
const TONES: Record<Tone, string> = {
  neutral: 'bg-surface text-ink-2',
  accent: 'bg-primary-soft text-primary-dark',
  navy: 'bg-secondary-soft text-secondary',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  info: 'bg-info-soft text-info',
};

export const Chip: React.FC<{ tone?: Tone; children: React.ReactNode; className?: string; title?: string }> = ({
  tone = 'neutral', children, className = '', title,
}) => (
  <span title={title} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[tone]} ${className}`}>
    {children}
  </span>
);
